import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { interval: { type: Number, default: 30000 } }

  connect() {
    this.handleFrameMissing = this.handleFrameMissing.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.element.addEventListener("turbo:frame-missing", this.handleFrameMissing)
    document.addEventListener("visibilitychange", this.handleVisibilityChange)
    this.timer = setInterval(() => this.poll(), this.intervalValue)
  }

  // Turbo's View#render keeps the active renderer in a single shared field.
  // When several frame reloads overlap (a background tab flushes queued
  // timers and responses all at once on wake-up), the later render deletes
  // that field while an earlier one is still awaiting it, and Turbo throws
  // "Cannot read properties of undefined (reading 'renderMethod')" as an
  // unhandled rejection. So: never reload while hidden, and never start a
  // reload while the previous one is still in flight.
  //
  // reload() also rejects with "Failed to fetch" when the polled request fails
  // at the network level (connection blip, navigation away). Swallow it: the
  // next tick retries on its own, and these aborts are not actionable errors
  // worth reporting to Honeybadger.
  poll() {
    if (document.hidden || this.reloading) return

    this.reloading = true
    return this.element.reload()
      .catch(() => {})
      .finally(() => { this.reloading = false })
  }

  disconnect() {
    clearInterval(this.timer)
    this.element.removeEventListener("turbo:frame-missing", this.handleFrameMissing)
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
  }

  // Catch up once when the admin returns to the tab, since polls were
  // skipped while it was hidden.
  handleVisibilityChange() {
    if (!document.hidden) this.poll()
  }

  // The frame polls every `interval` ms. If the admin session expires
  // between reloads, the auth gate answers with a 302 to the login page,
  // which Turbo follows to a 200 response that no longer contains this
  // frame. Instead of letting Turbo throw an unhandled rejection, turn
  // it into a full-page visit so the admin lands on the login screen.
  handleFrameMissing(event) {
    const { response, visit } = event.detail
    event.preventDefault()
    visit(response)
  }
}
